import { useCallback, useRef, useState } from 'react'
import { useAuth } from '../layout/AuthContext'
import { COLUMN_WIDTH_SAVE_DELAY_MS, DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../../shared/constants'

// Per-user column widths for a table, persisted in the user's preferences
// (preferences.tableColumns[tableId][colKey] = px). Reads the saved widths once
// on mount (the user is already loaded behind the auth gate), applies live during
// a drag, and saves debounced when the drag ends. Reusable by any DataTable.

interface ColLike { key: string, defaultWidth?: number }

export function useColumnWidths(tableId: string, columns: ColLike[]) {
    const { preferences, savePreferences } = useAuth()

    const [widths, setWidths] = useState<Record<string, number>>(() => {
        const saved = preferences.tableColumns?.[tableId] ?? {}
        const init: Record<string, number> = {}
        for (const c of columns) init[c.key] = saved[c.key] ?? c.defaultWidth ?? DEFAULT_COLUMN_WIDTH
        return init
    })

    // Latest values kept in refs so the drag handlers don't need to be re-created.
    const widthsRef = useRef(widths)
    widthsRef.current = widths
    const prefsRef = useRef(preferences)
    prefsRef.current = preferences

    const saveTimer = useRef<number | null>(null)
    const scheduleSave = useCallback(() => {
        if (saveTimer.current) window.clearTimeout(saveTimer.current)
        saveTimer.current = window.setTimeout(() => {
            const all = { ...(prefsRef.current.tableColumns ?? {}), [tableId]: widthsRef.current }
            savePreferences({ tableColumns: all }).catch(() => {})
        }, COLUMN_WIDTH_SAVE_DELAY_MS)
    }, [tableId, savePreferences])

    const startResize = useCallback((key: string, e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation()
        const startX = e.clientX
        const startW = widthsRef.current[key] ?? DEFAULT_COLUMN_WIDTH

        const onMove = (ev: MouseEvent) => {
            const w = Math.max(MIN_COLUMN_WIDTH, startW + (ev.clientX - startX))
            setWidths((prev) => ({ ...prev, [key]: w }))
        }
        const onUp = () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
            document.body.classList.remove('resizing-col')
            scheduleSave()
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
        document.body.classList.add('resizing-col')
    }, [scheduleSave])

    return { widths, startResize }
}
